import type { ReactNode } from "react";
import { EntityListPaneHeader } from "@/components/shared/EntityListPaneHeader";
import { EntitySearchInput } from "@/components/shared/EntitySearchInput";
import { EntityListLoadingState } from "@/components/shared/EntityListStates";
import { chromeSelectClass } from "@/lib/map-chrome";
import { cn } from "@/lib/utils";

interface EntityListPanelProps<T> {
  title: ReactNode;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Иконки справа от заголовка (создание / удаление и т.п.). */
  headerActions?: ReactNode;
  headerExtras?: React.ReactNode;
  /** Только тело списка (без aside/шапки) — для встраивания во внешний сайдбар. */
  embedded?: boolean;
  isLoading?: boolean;
  items: T[];
  selectedId: number | null;
  getId: (item: T) => number;
  renderItem: (item: T, selected: boolean) => React.ReactNode;
  /** Действия справа от строки (кнопка не вкладывается в основную кнопку выбора). */
  renderSideActions?: (item: T) => React.ReactNode;
  onSelect: (id: number) => void;
  isError?: boolean;
  errorText?: string;
  emptyText?: string;
}

export function EntityListPanel<T>({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Поиск...",
  headerActions,
  headerExtras,
  embedded = false,
  isLoading = false,
  items,
  selectedId,
  getId,
  renderItem,
  renderSideActions,
  onSelect,
  isError = false,
  errorText = "Не удалось загрузить список",
  emptyText = "Нет данных для отображения",
}: EntityListPanelProps<T>) {
  const listBody = (
    <>
      {isLoading ? (
        <EntityListLoadingState />
      ) : isError ? (
        <div className="p-3 text-sm text-destructive">{errorText}</div>
      ) : items.length === 0 ? (
        <div className="p-3 text-sm text-muted-foreground">{emptyText}</div>
      ) : (
        items.map((item) => {
          const id = getId(item);
          const selected = selectedId === id;
          // Как в EntityListItemShell (список объектов на карте): акцент слева у активной строки.
          const rowClass = cn(
            "rounded-none border-x-0 border-t-0 border-b border-sidebar-border hover:bg-sidebar-accent/80",
            selected && "bg-primary/10 dark:bg-primary/15 border-l-4 border-l-primary",
          );
          if (renderSideActions) {
            return (
              <div key={id} className={cn("flex w-full items-stretch min-h-0", rowClass)}>
                <button type="button" className="flex-1 min-w-0 cursor-pointer text-left p-3" onClick={() => onSelect(id)}>
                  {renderItem(item, selected)}
                </button>
                <div className="flex shrink-0 items-center pr-1">{renderSideActions(item)}</div>
              </div>
            );
          }
          return (
            <button
              key={id}
              type="button"
              className={cn("w-full cursor-pointer text-left p-3", rowClass)}
              onClick={() => onSelect(id)}
            >
              {renderItem(item, selected)}
            </button>
          );
        })
      )}
    </>
  );

  if (embedded) {
    return <div className="min-h-0">{listBody}</div>;
  }

  return (
    <aside className={cn("flex min-h-0 w-[380px] flex-col border-r border-border bg-sidebar text-sidebar-foreground", chromeSelectClass)}>
      <EntityListPaneHeader title="" hideTitle>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1 text-sm font-semibold">
            {typeof title === "string" ? <h2 className="truncate text-sm font-semibold">{title}</h2> : title}
          </div>
          {headerActions ? <div className="flex shrink-0 items-center gap-1">{headerActions}</div> : null}
        </div>
        <EntitySearchInput
          className="mt-2"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={onSearchChange}
        />
        {headerExtras ? <div className="mt-2">{headerExtras}</div> : null}
      </EntityListPaneHeader>
      <div className="flex-1 overflow-y-auto">{listBody}</div>
    </aside>
  );
}
